/*
  The plain words of this module. Everyday engineering words on every label (plain-words.md): the decided
  names of the element kinds, the match words, the kinds of value, the states of a document and of a spot.
  Standard names (DEXPI, CFIHOS, ...) appear only where a library or a mapping line is meant.
*/

import type {
  CoverageWord,
  DeliverableKind,
  ElementKind,
  LinkType,
  MatchWord,
  Phase,
  ReadLike,
  SpotKind,
  ValueKind,
} from "../../../api/types";
import { elementKindWord } from "../../../ui/words";

// The kinds of element are said the same way on every screen (UI-25): the table is ui/words.ts.
export { ELEMENT_KIND_WORDS as KIND_WORDS } from "../../../ui/words";

/** The word for a kind. A kind a newer server adds keeps its own text. */
export function kindWord(kind: string): string {
  return elementKindWord(kind);
}

/** The kinds of term the stored libraries hold, in everyday words (UI-16): the raw kind is never shown. */
export const TERM_KIND_WORDS: Record<string, string> = {
  class: "Class",
  type: "Type",
  property: "Property",
  property_set: "Group of properties",
  quantity_kind: "Kind of quantity",
  unit: "Unit",
  prefix: "Unit prefix",
  picklist: "Pick list",
  picklist_value: "Pick-list value",
};

export function termKindWord(kind: string): string {
  return TERM_KIND_WORDS[kind] ?? sentence(kind.replace(/_/g, " "));
}

export const MATCH_WORDS: Record<MatchWord, string> = {
  the_same: "The same",
  theirs_is_wider: "Theirs is wider",
  theirs_is_narrower: "Theirs is narrower",
  nearly_the_same: "Nearly the same",
  only_related: "Only related",
  no_match: "No match",
};

export const MATCH_HELP: Record<MatchWord, string> = {
  the_same: "Our term and theirs mean the same.",
  theirs_is_wider: "Their term covers ours and more.",
  theirs_is_narrower: "Their term is a smaller case of ours.",
  nearly_the_same: "Close, with a small difference worth writing down.",
  only_related: "Connected in meaning, not the same thing.",
  no_match: "Nothing in that library fits. Say why.",
};

export const MATCH_ORDER: MatchWord[] = ["the_same", "theirs_is_wider", "theirs_is_narrower", "nearly_the_same", "only_related", "no_match"];

export const VALUE_KIND_WORDS: Record<ValueKind, string> = {
  text: "Text",
  whole_number: "Whole number",
  number: "Number with a unit",
  choice: "Choice from a pick list",
  yes_no: "Yes or no",
  date: "Date",
  size_designation: "Size designation",
  project_code: "Code from a project list",
  table: "Table",
};

export const VALUE_KIND_ORDER: ValueKind[] = ["text", "whole_number", "number", "choice", "size_designation", "project_code", "yes_no", "date", "table"];

export const LINK_TYPE_WORDS: Record<LinkType, string> = {
  is_shown_on: "is shown on",
  is_described_by: "is described by",
  is_specified_by: "is specified by",
};

export const DELIVERABLE_KIND_WORDS: Record<DeliverableKind, string> = {
  A: "A: generated fully from data",
  B: "B: data plus a drawing tool",
  C: "C: geometric",
};

export const PHASES: Phase[] = ["Conceptual", "Basic", "Detail"];

export const SPOT_KIND_WORDS: Record<SpotKind, string> = {
  datasheet_line: "Line on a sheet",
  document_line: "Line in a document",
  list_column: "List column",
  drawing_tag: "Tag on a drawing",
  symbol: "Symbol",
  connection: "Connection between symbols",
  requirement: "Requirement",
  title_block_field: "Title block field",
  revision_box_field: "Revision box field",
  instruction_like_text: "Instruction-like text",
  picture: "Picture",
  other: "Other",
};

export function spotKindWord(kind: string): string {
  return (SPOT_KIND_WORDS as Record<string, string>)[kind] ?? kind.replace(/_/g, " ");
}

export const COVERAGE_WORDS: Record<CoverageWord, string> = {
  covered: "Covered",
  waiting_for_sme: "Waiting for the SME",
  proposed: "Proposed",
  missing: "Missing",
  not_data: "Not data",
  left_to_other_discipline: "Left to another discipline",
  belongs_to_other_item: "Belongs to another item",
};

export const READER_STATUS_WORDS: Record<string, string> = {
  uploaded: "Uploaded, not read yet",
  reading: "Being read",
  read: "Read",
  partly_read: "Partly read",
  cancelled: "Reading was cancelled",
  failed: "Could not be read",
};

export function readerStatusWord(status: string): string {
  return READER_STATUS_WORDS[status] ?? status.replace(/_/g, " ");
}

export const READ_LIKE_WORDS: Record<ReadLike, string> = {
  datasheet: "A datasheet",
  pid: "A P&ID",
  pfd: "A process flow diagram",
  sld: "A single-line diagram",
  architecture: "An instrumentation and control architecture drawing",
  equipment_list: "An equipment list",
  specification: "A specification",
  layout: "A layout drawing",
  calculation_note: "A calculation note",
};

export const READ_LIKE_ORDER: ReadLike[] = ["datasheet", "pid", "pfd", "sld", "architecture", "equipment_list", "specification", "layout", "calculation_note"];

// The kinds of line are said the same way on every screen (UI-25): see ui/words.ts.
export { LINE_KIND_WORDS, lineKindWord } from "../../../ui/words";

export const FIELD_ORIGIN_WORDS: Record<string, string> = {
  item: "this item",
  parent: "its parent item",
  capability: "a capability",
  component_kind: "a component kind",
  other_discipline: "another discipline",
};

export const FILE_KIND_WORDS: Record<string, string> = {
  item: "Item files",
  shared: "Shared files",
  mapping: "Library mapping",
  revision_record: "Revision record",
  specification: "Specification",
  symbol: "Symbols and line styles",
};

export const FILE_KIND_ORDER = ["item", "shared", "mapping", "revision_record", "specification", "symbol"];

/** The kinds of element a person can add by hand with a form (2.4.2). Libraries and the framework are loaded only. */
export const ADDABLE_KINDS = [
  "item",
  "family",
  "sub_family",
  "component_kind",
  "component",
  "capability",
  "data_field",
  "data_group",
  "pick_list",
  "document_kind",
  "document_link",
  "link",
] as const satisfies readonly ElementKind[];

export type AddableKind = (typeof ADDABLE_KINDS)[number];

export function isAddableKind(value: string | null | undefined): value is AddableKind {
  return !!value && (ADDABLE_KINDS as readonly string[]).includes(value);
}

/** The first letters of a sentence in capitals, for a label made from a raw word. */
export function sentence(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}
